import { styled, alpha, type Theme } from '@mui/material/styles';
import { Box, TextField, Button, Grid, Chip, Paper } from '@mui/material';

// Container principal
export const FiltersContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  backgroundColor: 'transparent',
  marginBottom: theme.spacing(2),
}));

// Grid container
export const FiltersGrid = styled(Grid)(({ theme }) => ({
  alignItems: 'center',
}));

const outlinedInputSurface = (theme: Theme) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    default: isDark
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.white, 0.9),
    hover: isDark
      ? alpha(theme.palette.common.white, 0.12)
      : alpha(theme.palette.common.white, 0.95),
    focused: isDark ? alpha(theme.palette.common.white, 0.14) : theme.palette.background.paper,
  };
};

// TextField de búsqueda principal
export const SearchTextField = styled(TextField)(({ theme }) => {
  const surface = outlinedInputSurface(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1.5),
      backgroundColor: surface.default,
      backdropFilter: 'blur(10px)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: surface.hover,
        transform: 'translateY(-1px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 4px 12px rgba(0, 0, 0, 0.35)'
            : '0 4px 12px rgba(0, 0, 0, 0.1)',
      },
      '&.Mui-focused': {
        backgroundColor: surface.focused,
        transform: 'translateY(-1px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`
            : '0 4px 20px rgba(59, 130, 246, 0.15)',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: theme.palette.text.secondary,
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      '& legend': {
        maxWidth: 'none',
        width: 'fit-content',
        minWidth: 0,
        padding: '0 8px',
        boxSizing: 'content-box',
      },
    },
  };
});

// TextField estándar
export const StandardTextField = styled(TextField)(({ theme }) => {
  const surface = outlinedInputSurface(theme);
  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(1.5),
      backgroundColor: surface.default,
      backdropFilter: 'blur(10px)',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: surface.hover,
        transform: 'translateY(-1px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 4px 12px rgba(0, 0, 0, 0.35)'
            : '0 4px 12px rgba(0, 0, 0, 0.1)',
      },
      '&.Mui-focused': {
        backgroundColor: surface.focused,
        transform: 'translateY(-1px)',
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`
            : '0 4px 20px rgba(59, 130, 246, 0.15)',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: theme.palette.text.secondary,
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      '& legend': {
        maxWidth: 'none',
        width: 'fit-content',
        minWidth: 0,
        padding: '0 8px',
        boxSizing: 'content-box',
      },
    },
  };
});

// Botones
export const SearchButton = styled(Button)(({ theme }) => ({
  minWidth: 110,
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  transition: 'all 0.2s ease-in-out',
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: 'white',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
  },
}));

export const ResetButton = styled(Button)(({ theme }) => ({
  minWidth: 110,
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  transition: 'all 0.2s ease-in-out',
  borderColor: theme.palette.divider,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: theme.shadows[1],
    transform: 'translateY(-2px)',
  },
}));

export const MoreFiltersButton = styled(Button)(({ theme }) => ({
  minWidth: 110,
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  transition: 'all 0.2s ease-in-out',
  borderColor: theme.palette.divider,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: 'transparent',
    boxShadow: theme.shadows[1],
    transform: 'translateY(-2px)',
  },
}));

// Container para filtros adicionales
export const MoreFiltersContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// Chips de filtros activos
export const ActiveFiltersChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

export const ActiveFiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

// Container de botones
export const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

// Table container
export const TableContainer = styled(Paper)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
}));

// Empty state container
export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
}));
